<template>
    <div class="bg-center bg-cover border h-full relative rounded-md" :class="cardDynamicClasses" style="background-image: url(/images/background-pattern.png);">
        <div class="flex flex-col items-center">
            <template v-if="userAdminsOrganization">
                <div v-if="currentStatus !== 'active' && application.slug !== 'budgets'"
                    class="btn btn-primary btn-xs self-end mt-2 mr-2"
                    :class="actionsDropdownDynamicClasses"
                    v-tooltip="{
                        content: $t('organization_applications.closed_beta_info', {
                            emphasizedOpen: '<span class=&quot;font-semibold&quot;>',
                            name: application.name,
                            emphasizedClose: '</span>',
                        }),
                        placement: 'top'
                    }">
                    <span class="icon icon-times icon-lg icon-fw"></span>
                    {{ $t('organization_applications.closed_beta') }}
                    <span class="icon icon-question-circle icon-fw"></span>
                </div>
                <div v-else class="btn-group btn-group-xs dropdown self-end mt-2 mr-2">
                    <button :disabled="! ['active', null].includes(currentStatus)" class="btn btn-primary btn-xs dropdown-toggle" :class="actionsDropdownDynamicClasses" data-toggle="dropdown" type="button" aria-expanded="false">
                        <span class="icon icon-cog icon-lg icon-fw"></span>
                        {{ $t('globals.actions') }}
                        <span class="caret"></span>
                    </button>
                    <ul class="dropdown-menu dropdown-menu-right">
                        <li v-if="currentStatus === null">
                            <a @click="installApplication()" href="javascript:;">
                                <div class="media">
                                    <div class="media-left">
                                        <span class="icon icon-plus-square-o"></span>
                                    </div>
                                    <div class="media-body">
                                        <span class="d-b">{{ $t('organization_applications.install') }}</span>
                                    </div>
                                </div>
                            </a>
                        </li>
                        <li v-if="currentStatus === 'active'">
                            <a href="/organization/users#active-users">
                                <div class="media">
                                    <div class="media-left">
                                        <span class="icon icon-users"></span>
                                    </div>
                                    <div class="media-body">
                                        <span class="d-b">{{ $t('dashboard.access_and_roles') }}</span>
                                    </div>
                                </div>
                            </a>
                        </li>
                        <li v-if="currentStatus === 'active'">
                            <a href="/organization/payments#subscriptions">
                                <div class="media">
                                    <div class="media-left">
                                        <span class="icon icon-ticket"></span>
                                    </div>
                                    <div class="media-body">
                                        <span class="d-b">{{ $t('globals.subscription_plan') }}</span>
                                    </div>
                                </div>
                            </a>
                        </li>
                        <li v-if="currentStatus === 'active'">
                            <a @click="deleteApplication()" href="javascript:;">
                                <div class="media">
                                    <div class="media-left">
                                        <span class="icon icon-trash-o"></span>
                                    </div>
                                    <div class="media-body">
                                        <span class="d-b">{{ $t('globals.delete') }}</span>
                                    </div>
                                </div>
                            </a>
                        </li>
                    </ul>
                </div>
            </template>
            <h2 class="font-thin text-center text-xl text-white tracking-wide my-0 pt-6">{{ application.name }}</h2>
            <div class="py-8">
                <slot>
                    <svg class="fill-current relative text-white w-20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><circle cx="256" cy="256" fill="#fff" r="256" data-original="#1CE8B7" data-old_color="#1ce8b7"/><path d="M511.822 265.084L314.851 68.113l.739 25.654-26.879-26.879-60.497 106.817-78.762 53.541 33.234 33.234-20.357 28.699-52.697-52.697 20.744 97.745-26.661 37.585 139.887 139.887c4.108.193 8.24.301 12.398.301 138.343 0 251.036-109.741 255.822-246.916z" fill="#f7fafc" data-original="#1DCC9E" data-old_color="#1dcc9e"/><path d="M431.726 301.68l-37.304-33.293-14.914 16.711-18.698-35.489-44.236 23.306 27.397 52.001-10.625 11.905-44.635 12.201v-67.377l76.658-50.621 41.038 7.273 8.726-49.233-28.634-5.074 8.694-26.623-47.529-15.522-17.051 52.213-41.902 27.669v-70.849l56.438-42.991-30.298-39.774-26.14 19.911V66.887h-65v92.646l-52.083-51.93-35.303 35.408 22.545 22.478-33.016 17.675 23.599 44.081 46.427-24.855 27.831 27.749v91.568l-53.416-13.142 29.447-48.238-42.677-26.052-20.866 34.182-26.567-31.977-38.459 31.952 40.633 48.908-22.145 6.485 14.054 47.985 52.206-15.29 67.79 16.677v57.096h65v-29.437l59.805-16.347 72.249 11.209 7.665-49.409-32.091-4.979z" fill="#74d9e7" data-original="#FF6155" data-old_color="#ff6155"/><path d="M431.726 301.68l-37.304-33.293-14.914 16.711-18.698-35.489-44.236 23.306 27.397 52.001-10.625 11.905-44.635 12.201v-67.377l76.658-50.621 41.038 7.273 8.726-49.233-28.634-5.074 8.694-26.623-47.529-15.522-17.051 52.213-41.902 27.669v-70.849l56.438-42.991-30.298-39.774-26.14 19.911V66.887h-33.116v363.406h33.116v-29.437l59.805-16.347 72.249 11.209 7.665-49.409-32.091-4.979z" fill="#38c8dd" data-original="#ED2415" class="active-path" data-old_color="#ed2415"/></svg>
                </slot>
            </div>
            <div class="pb-6">
                <div v-if="currentStatus === 'active'">
                    <button v-if="organizationApplicationUserId" @click="connectToApplication" :disabled="connectorToApplicationForm.busy" class="btn btn-sm cursor-pointer font-semibold px-4 py-5px rounded-sm rocket-button uppercase disabled:opacity-100" :class="connectButtonDynamicClasses">
                        <div class="default">{{ $t('dashboard.connect') }}</div>
                        <div class="success">
                            <svg>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 13 11" id="check">
                                    <polyline stroke="currentColor" points="1 5.5 5 9.5 12 1.5"></polyline>
                                </svg>
                            </svg>
                            <div>{{ $t('dashboard.connected') }}</div>
                        </div>
                        <div class="animation">
                            <div class="rocket">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" id="rocket">
                                    <path
                                        d="M12,0 C18.6666667,8.70175439 19.7777778,19.0350877 15.3333333,31 L8.66666667,31 C4.22222222,19.0350877 5.33333333,8.70175439 12,0 Z"
                                        fill="#eef0fd"></path>
                                    <path
                                        d="M12,0 C5.33333333,8.70175439 4.22222222,19.0350877 8.66666667,31 C6.72222222,17.9473684 7.83333333,7.61403509 12,0 Z"
                                        fill="#fff"></path>
                                    <path
                                        d="M12,0 C18.6666667,8.70175439 19.7777778,19.0350877 15.3333333,31 C17.2777778,17.9473684 16.1666667,7.61403509 12,0 Z"
                                        fill="#d3d4ec"></path>
                                    <path
                                        d="M22.2399372,27.25 C21.2403105,25.558628 19.4303122,23.808628 16.21,22 L15,31 L17.6512944,31 C18.2564684,31 18.8216022,31.042427 19.1572924,31.5292747 L21.7379379,35.271956 C22.0515593,35.7267976 22.5795404,36 23.1449294,36 C23.5649145,36 23.9142153,35.7073938 23.9866527,35.3215275 L24,35.146217 L23.9987214,35.1196135 C23.7534506,31.4421183 23.1671892,28.8189138 22.2399372,27.25 Z"
                                        fill="#c2c3d9"></path>
                                    <path
                                        d="M1.76006278,27.25 C2.75968951,25.558628 4.56968777,23.808628 7.79,22 L9,31 L6.34870559,31 C5.74353157,31 5.17839777,31.042427 4.84270762,31.5292747 L2.2620621,35.271956 C1.94844071,35.7267976 1.42045963,36 0.855070627,36 C0.435085457,36 0.0857846604,35.7073938 0.0133472633,35.3215275 L0,35.146217 L0.00127855763,35.1196135 C0.24654935,31.4421183 0.832810758,28.8189138 1.76006278,27.25 Z"
                                        fill="#d3d4ec"></path>
                                    <circle fill="#c2c3d9" cx="12" cy="12" r="3"></circle>
                                    <circle fill="#275efe" cx="12" cy="12" r="2.5"></circle>
                                    <path
                                        d="M15.6021597,5.99977504 L8.39784027,5.99977504 C8.54788101,5.6643422 8.70496315,5.3309773 8.86908669,4.99968036 L15.1309133,4.99968036 C15.2950369,5.3309773 15.452119,5.6643422 15.6021597,5.99977504 Z"
                                        fill-opacity="0.3" fill="#9ea0be"></path>
                                </svg>
                            </div>
                            <div class="smoke">
                                <i></i><i></i><i></i><i></i><i></i><i></i>
                            </div>
                        </div>
                    </button>
                    <div v-else class="cursor-default btn btn-sm font-semibold px-4 py-5px rounded-sm uppercase" :class="ctaButtonDynamicClasses">
                        {{ $t('dashboard.no_access') }}
                        <span v-tooltip="{ content: $t('dashboard.no_access_info'), placement: 'top' }" class="cursor-pointer icon icon-question-circle icon-lg icon-fw"></span>
                    </div>
                </div>
                <div v-else-if="currentStatus === null">
                    <div class="bg-gray-200 cursor-default text-gray-600 btn btn-sm font-semibold px-4 py-5px rounded-sm uppercase">
                        {{ $t('dashboard.unavailable') }}
                        <span v-tooltip="{ content: $t('dashboard.unavailable_info'), placement: 'top' }" class="cursor-pointer icon icon-question-circle icon-lg icon-fw"></span>
                    </div>
                </div>
                <div v-else-if="currentStatus === 'installing'">
                    <div v-if="userAdminsOrganization" class="bg-gray-200 cursor-default text-gray-600 btn btn-sm font-semibold px-4 py-5px rounded-sm uppercase">
                        <span class="icon icon-spinner icon-lg icon-fw spin"></span>
                        {{ $t('dashboard.installing') }}
                    </div>
                    <div v-else class="bg-gray-200 cursor-default text-gray-600 btn btn-sm font-semibold px-4 py-5px rounded-sm uppercase">
                        {{ $t('dashboard.unavailable') }}
                        <span v-tooltip="{ content: $t('dashboard.unavailable_info'), placement: 'top' }" class="cursor-pointer icon icon-question-circle icon-lg icon-fw"></span>
                    </div>
                </div>
                <div v-else-if="currentStatus === 'installation_failed'">
                    <div v-if="userAdminsOrganization" class="bg-gray-200 cursor-default text-gray-600 btn btn-sm font-semibold px-4 py-5px rounded-sm uppercase">
                        {{ $t('dashboard.installation_failed') }}
                        <span v-tooltip="{ content: $t('dashboard.installation_failed_info'), placement: 'top' }" class="cursor-pointer icon icon-question-circle icon-lg icon-fw"></span>
                    </div>
                    <div v-else class="bg-gray-200 cursor-default text-gray-600 btn btn-sm font-semibold px-4 py-5px rounded-sm uppercase">
                        {{ $t('dashboard.unavailable') }}
                        <span v-tooltip="{ content: $t('dashboard.unavailable_info'), placement: 'top' }" class="cursor-pointer icon icon-question-circle icon-lg icon-fw"></span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const charming = require('charming');

export default {
    props: {
        application: {
            type: Object,
            required: true,
        },
        isEditable: {
            type: Boolean,
            required: true,
        },
    },

    data() {
        return {
            currentStatus: this.application.status,
            organizationApplicationUserId: this.application.organization_application_user_id,
            form: new Form({
                organization_application_user_id: '',
                session_data: null,
            }),
            connectButtonAnimationClassesList: [],
            applicationInstance: null,
            applicationForm: new Form({
                user_id: '',
                token: '',
            }),
            connectorToApplicationForm: new Form({}),
        };
    },

    computed: {
        cardDynamicClasses() {
            let classes;
            switch (true) {
                case (this.currentStatus === 'active' && this.application.color === 'purple'):
                    classes = 'bg-purple-500 border-purple-600';
                    break;
                case (this.currentStatus !== 'active' && this.application.color === 'purple'):
                    classes = 'bg-gray-500 border-gray-card';
                    break;
                case (this.currentStatus === 'active' && this.application.color === 'red'):
                    classes = 'bg-red-500 border-red-600';
                    break;
                case (this.currentStatus !== 'active' && this.application.color === 'red'):
                    classes = 'bg-gray-500 border-gray-card';
                    break;
                case (this.currentStatus === 'active' && this.application.color === 'blue'):
                    classes = 'bg-blue-500 border-blue-600';
                    break;
                case (this.currentStatus !== 'active' && this.application.color === 'blue'):
                    classes = 'bg-gray-500 border-gray-card';
                    break;
            }

            return classes;
        },

        addButtonDynamicClasses() {
            let groupHoverTextColor;
            switch (true) {
                case (this.application.color === 'purple'):
                    groupHoverTextColor = 'group-hover:text-gray-200';
                    break;
                case (this.application.color === 'red'):
                    groupHoverTextColor = 'group-hover:text-gray-200';
                    break;
                case (this.application.color === 'blue'):
                    groupHoverTextColor = 'group-hover:text-gray-200';
                    break;
            }

            return groupHoverTextColor;
        },

        ctaButtonDynamicClasses() {
            let classes;
            switch (true) {
                case (this.application.color === 'purple'):
                    classes = 'bg-purple-300 hover:bg-purple-200 text-purple-600';
                    break;
                case (this.application.color === 'red'):
                    classes = 'bg-red-300 hover:bg-red-200 text-red-600';
                    break;
                case (this.application.color === 'blue'):
                    classes = 'bg-blue-300 hover:bg-blue-200 text-blue-600';
                    break;
            }

            return classes;
        },

        actionsDropdownDynamicClasses() {
            let classes;
            switch (true) {
                case (this.currentStatus !== 'active'):
                    classes = 'bg-gray-300 border-gray-300 hover:bg-gray-200 text-gray-600';
                    break;
                case (this.application.color === 'purple'):
                    classes = 'bg-purple-300 border-purple-300 hover:bg-purple-200 text-purple-600';
                    break;
                case (this.application.color === 'red'):
                    classes = 'bg-red-300 border-red-300 hover:bg-red-200 text-red-600';
                    break;
                case (this.application.color === 'blue'):
                    classes = 'bg-blue-300 border-blue-300 hover:bg-blue-200 text-blue-600';
                    break;
            }

            return classes;
        },

        connectButtonDynamicClasses() {
            let classes = this.ctaButtonDynamicClasses;
            let animationClasses = this.connectButtonAnimationClassesList.join(' ');

            return [classes, animationClasses].join(' ');
        },

        userAdminsOrganization() {
            return window.Globals.user.is_admin || window.Globals.user.organization.owner_id === window.Globals.user.id;
        },

        sessionData() {
            return window.Globals.sessionData ?? [];
        },
    },

    created() {
        if (this.userAdminsOrganization && this.application.pivot) {
            this.listenForStatusUpdates();
        }
    },

    mounted() {
        const d = 40;

        document.querySelectorAll('.rocket-button').forEach(button => {
            button.querySelectorAll('.default, .success > div').forEach(text => {
                charming(text);
                text.querySelectorAll('span').forEach((span, i) => {
                    span.innerHTML = span.textContent == ' ' ? '&nbsp;' : span.textContent;
                    span.style.setProperty('--d', i * d + 'ms');
                    span.style.setProperty('--ds', text.querySelectorAll('span').length * d - d - i * d + 'ms');
                });
            });
        });
    },

    methods: {
        deleteApplication() {
            Bus.$emit('showDeleteApplicationModal', { application: this.application });
        },

        installApplication() {
            Bus.$emit('showInstallApplicationModal', { application: this.application });
        },

        listenForStatusUpdates() {
            window.Echo.private(`Admin.OrganizationApplication.${this.application.pivot.id}`)
                .listen('.application.status_updated', response => {
                    this.currentStatus = response.status;

                    if (this.currentStatus === 'active') {
                        axios.get('/api/v1/user-access', { params: { organization_application_id: this.application.pivot.id } })
                            .then(response => this.organizationApplicationUserId = response.data.id ? response.data.id : null)
                            .catch(() => {});
                    }
                });
        },

        connectToApplication() {
            this.connectorToApplicationForm.startProcessing();
            this.form.organization_application_user_id = this.organizationApplicationUserId;
            this.form.session_data = this.sessionData;

            Globals.post('/api/v1/authentication-token', this.form)
                .then(response => {
                    this.applicationInstance = axios.create({ baseURL: this.application.url, withCredentials: true });
                    this.applicationForm.user_id = response.user_access.user_id;
                    this.applicationForm.token = response.token;
                })
                .then(() => this.applicationInstance.post('/login', this.applicationForm))
                .then(() => this.startButtonAnimation())
                .then(() => new Promise(resolve => setTimeout(resolve, 3000)))
                .then(() => window.location.href = this.application.url)
                .then(() => this.form.finishProcessing())
                .catch(error => {
                    const errors = this.form.errors.any() ? this.form.errors.flatten() : this.applicationForm.errors.flatten();
                    const message = errors.length > 0 ? errors[0] : this.$t('globals.unexpected_error');
                    window.toastr.clear();
                    window.toastr.error(message);
                    this.form.finishProcessing();
                })
                .finally(() => {
                    this.resetForms();
                    this.applicationInstance = null;
                });
        },

        resetForms() {
            this.form = new Form({
                organization_application_user_id: '',
                session_data: null,
            });
            this.applicationForm = new Form({
                user_id: '',
                token: '',
            });
            this.connectorToApplicationForm = new Form({});
        },

        startButtonAnimation() {
            if (this.connectButtonAnimationClassesList.includes('animated')) {
                return;
            }
            this.connectButtonAnimationClassesList.push('animated');
            if (this.connectButtonAnimationClassesList.includes('live')) {
                this.connectButtonAnimationClassesList = this.connectButtonAnimationClassesList.filter(className => className === 'live');
            } else {
                this.connectButtonAnimationClassesList.push('live');
            }
        },
    },
};
</script>
